<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { formatDate, addThousandsSeparator } from '~/utils/utils.js';
import Card from '~/components/Card.vue';
import InfoTooltip from '~/components/InfoTooltip.vue';
import LoadingSkeleton from '~/components/LoadingSkeleton.vue';
import CardLabel from '~/components/CardLabel.vue';

// Props
const props = defineProps({
    currentBlockReward: { type: Number, default: 0 },
    currentBlockRewardUsd: { type: Number, default: 0 },
    nextBlockReward: { type: Number, default: 0 },
    nextBlockRewardUsd: { type: Number, default: 0 },
    nextHalvingDate: { type: String, default: '' },
    rewardPercentageDecrease: { type: Number, default: 0 },
    loading: { type: Boolean, default: false }
});

// Data for the countdown timer
const days = ref(0);
const hours = ref(0);
const minutes = ref(0);
const seconds = ref(0);
let interval = null;

// Target date for the next halving
const targetDate = computed(() => props.nextHalvingDate);

// Calculate time left for the next halving
const calculateTimeLeft = () => {
    if (!targetDate.value) return;

    // Calculate the difference between the target date and now
    const now = new Date().getTime();
    const target = new Date(targetDate.value).getTime();
    const difference = target - now;

    // Calculate days, hours, minutes and seconds
    if (difference > 0) {
        days.value = Math.floor(difference / (1000 * 60 * 60 * 24));
        hours.value = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        minutes.value = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        seconds.value = Math.floor((difference % (1000 * 60)) / 1000);
    }
};

// On component mounted
onMounted(() => {
    calculateTimeLeft();
    interval = setInterval(calculateTimeLeft, 1000);
});

// On component unmounted
onUnmounted(() => {
    if (interval) clearInterval(interval);
});
</script>

<template>
    <div class="grid grid-cols-1 cards-gap">
        <Card>
            <div class="flex flex-col lg:flex-row lg:items-center lg:space-x-8 space-y-8 lg:space-y-0">
                <!-- Current Reward -->
                <div class="flex-1 text-center lg:text-left">
                    <div class="flex items-center justify-center lg:justify-start mb-2">
                        <CardLabel text="Current Block Reward" />
                        <InfoTooltip text="The amount of KAS rewarded for mining each block" />
                    </div>
                    <div>
                        <div class="mb-4">
                            <LoadingSkeleton v-if="props.loading" height="36px" width="140px" class="mx-auto lg:mx-0" />
                            <p v-else class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-transparent bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent slide-in-up">
                                {{ props.currentBlockReward }} KAS
                            </p>
                        </div>

                        <!-- Reward in USD -->
                        <LoadingSkeleton v-if="props.loading" height="48px" width="115px" class="mx-auto lg:mx-0" />
                        <div v-else class="inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-gray-100 to-purple-100 dark:from-gray-700 dark:to-purple-900/50 slide-in-up">
                            <svg class="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span class="text-base sm:text-lg font-semibold text-gray-500 dark:text-gray-300">${{ addThousandsSeparator(props.currentBlockRewardUsd, 2) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Middle Section -->
                <div class="flex flex-col items-center justify-center px-2 sm:px-4">
                    <div class="flex items-center justify-center">
                        <!-- Percentage decrease -->
                        <div class="me-3">
                            <LoadingSkeleton v-if="props.loading" height="48px" width="115px" />
                            <div v-else class="inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-red-100 to-orange-100 dark:from-red-900/50 dark:to-orange-900/50 slide-in-up">
                                <svg class="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                </svg>
                                <span class="text-base sm:text-lg font-semibold text-gray-500 dark:text-gray-300">-{{ addThousandsSeparator(rewardPercentageDecrease, 2) }}%</span>
                            </div>
                        </div>

                        <!-- Arrow icon -->
                        <svg class="w-8 h-8 lg:w-10 lg:h-10 text-gray-400/70 dark:text-gray-500 rotate-90 lg:rotate-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </div>

                    <!-- Next Reduction -->
                    <div class="mt-4 lg:mt-6 text-center space-y-4">
                        <LoadingSkeleton v-if="props.loading" height="36px" width="140px" class="mx-auto" />
                        <span v-else class="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-gray-50 dark:bg-gray-700/50 text-xs sm:text-sm text-gray-600 dark:text-gray-300 slide-in-up">
                            <svg class="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Next Reduction: {{ formatDate(props.nextHalvingDate) }}
                        </span>

                        <!-- Countdown Timer -->
                        <LoadingSkeleton v-if="props.loading" height="64px" width="360px" class="mx-auto" />
                        <div v-else class="flex flex-wrap justify-center items-center gap-2 text-xs sm:text-sm slide-in-up">
                            <div class="bg-white dark:bg-gray-700/50 px-3 sm:px-4 py-2 rounded-lg sm:min-w-[70px]">
                                <span class="block text-lg sm:text-xl font-bold bg-gradient-to-br from-purple-600 to-blue-500 bg-clip-text text-transparent">{{ days }}</span>
                                <span class="text-gray-500 dark:text-gray-400 text-xs font-medium">days</span>
                            </div>
                            <div class="text-gray-300 dark:text-gray-600 font-bold text-lg px-0.5">:</div>
                            <div class="bg-white dark:bg-gray-700/50 px-3 sm:px-4 py-2 rounded-lg sm:min-w-[70px]">
                                <span class="block text-lg sm:text-xl font-bold bg-gradient-to-br from-blue-500 to-cyan-500 bg-clip-text text-transparent">{{ hours }}</span>
                                <span class="text-gray-500 dark:text-gray-400 text-xs font-medium">hrs</span>
                            </div>
                            <div class="text-gray-300 dark:text-gray-600 font-bold text-lg px-0.5">:</div>
                            <div class="bg-white dark:bg-gray-700/50 px-3 sm:px-4 py-2 rounded-lg sm:min-w-[70px]">
                                <span class="block text-lg sm:text-xl font-bold bg-gradient-to-br from-cyan-500 to-teal-500 bg-clip-text text-transparent">{{ minutes }}</span>
                                <span class="text-gray-500 dark:text-gray-400 text-xs font-medium">min</span>
                            </div>
                            <div class="text-gray-300 dark:text-gray-600 font-bold text-lg px-0.5">:</div>
                            <div class="bg-white dark:bg-gray-700/50 px-3 sm:px-4 py-2 rounded-lg sm:min-w-[70px]">
                                <span class="block text-lg sm:text-xl font-bold bg-gradient-to-br from-teal-500 to-green-500 bg-clip-text text-transparent">{{ seconds }}</span>
                                <span class="text-gray-500 dark:text-gray-400 text-xs font-medium">sec</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Next Reward -->
                <div class="flex-1 text-center lg:text-right">
                    <div class="flex items-center justify-center lg:justify-end mb-2">
                        <CardLabel text="Next Block Reward" />
                        <InfoTooltip text="The amount of KAS rewarded for mining each block after the next reward reduction" />
                    </div>
                    <div>
                        <div class="mb-4">
                            <LoadingSkeleton v-if="props.loading" height="36px" width="140px" class="ml-auto" />
                            <p v-else class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-transparent bg-gradient-to-r from-blue-500 to-teal-500 bg-clip-text text-transparent slide-in-up">
                                {{ props.nextBlockReward }} KAS
                            </p>
                        </div>

                        <!-- Reward in USD -->
                        <LoadingSkeleton v-if="props.loading" height="48px" width="115px" class="ml-auto" />
                        <div v-else class="inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-gray-100 to-teal-100 dark:from-gray-700 dark:to-teal-900/50 slide-in-up">
                            <svg class="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span class="text-base sm:text-lg font-semibold text-gray-500 dark:text-gray-300">${{ addThousandsSeparator(props.nextBlockRewardUsd, 2) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    </div>
</template>